import { useEffect, useMemo, useState, useCallback } from "react";
import { kitchenLogin, fetchKitchenOrders, updateOrderStatus, deleteOrder, deleteAllOrders, fetchKitchenSlotFills } from "@/lib/api";
import { Loader2, Lock, RefreshCw, LogOut, Phone, Clock, ListChecks, Settings, Trash2, Trash } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import KitchenSettings from "@/pages/KitchenSettings";

const HTTP_UNAUTHORIZED = 401;
const ORDERS_POLL_MS = 15000;

const STATUSES = [
  { key: "new", label: "New", color: "bg-[#D9381E] text-white" },
  { key: "preparing", label: "Preparing", color: "bg-[#B77714] text-white" },
  { key: "ready", label: "Ready", color: "bg-[#2C5E3B] text-white" },
  { key: "completed", label: "Completed", color: "bg-[#7A6C5D] text-white" },
  { key: "cancelled", label: "Cancelled", color: "bg-[#2C241B] text-white" },
];

export default function Kitchen() {
  const [token, setToken] = useState(() => localStorage.getItem("hafa_kitchen_token") || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("orders");
  const [slotFills, setSlotFills] = useState({ today: null, tomorrow: null, cap: null });

  const loadSlotFills = useCallback(async (t = token) => {
    if (!t) return;
    const toIso = (d) => d.toISOString().slice(0, 10);
    const today = new Date(); today.setHours(0,0,0,0);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    try {
      const [a, b] = await Promise.all([
        fetchKitchenSlotFills(toIso(today), t),
        fetchKitchenSlotFills(toIso(tomorrow), t),
      ]);
      setSlotFills({ today: a, tomorrow: b, cap: a?.cap ?? null });
    } catch (err) {
      // best-effort polling — never surface to user, but log in dev for triage
      if (process.env.NODE_ENV !== "production") console.debug("slot-fills poll failed", err);
    }
  }, [token]);

  const load = useCallback(async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const list = await fetchKitchenOrders(t);
      setOrders(list);
    } catch (e) {
      if (e?.response?.status === HTTP_UNAUTHORIZED) {
        localStorage.removeItem("hafa_kitchen_token");
        setToken("");
        toast.error("Session expired");
      } else {
        toast.error("Failed to load orders");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token && tab === "orders") {
      load();
      loadSlotFills();
      const iv = setInterval(() => { load(); loadSlotFills(); }, ORDERS_POLL_MS);
      return () => clearInterval(iv);
    }
  }, [token, tab, load, loadSlotFills]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const t = await kitchenLogin(password);
      localStorage.setItem("hafa_kitchen_token", t);
      setToken(t);
      toast.success("Logged in");
    } catch {
      toast.error("Incorrect password");
    } finally {
      setLoggingIn(false);
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const updated = await updateOrderStatus(id, status, token);
      setOrders(prev => prev.map(o => o.id === id ? updated : o));
      if (status === "cancelled") loadSlotFills();
      toast.success(`Marked ${STATUSES.find(s => s.key === status)?.label}`);
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this order permanently? This can't be undone.")) return;
    try {
      await deleteOrder(id, token);
      setOrders(prev => prev.filter(o => o.id !== id));
      loadSlotFills();
      toast.success("Order deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteAll = async () => {
    if (orders.length === 0) return;
    const msg = `Delete ALL ${orders.length} orders permanently? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    if (!window.confirm("Are you absolutely sure? Type OK on the next prompt to confirm.")) return;
    try {
      const res = await deleteAllOrders(token);
      setOrders([]);
      toast.success(`Deleted ${res.deleted} orders`);
    } catch {
      toast.error("Failed to delete all");
    }
  };

  const logout = () => {
    localStorage.removeItem("hafa_kitchen_token");
    setToken("");
    setOrders([]);
  };

  const filtered = useMemo(
    () => (filter === "all" ? orders : orders.filter(o => o.status === filter)),
    [orders, filter]
  );
  const stats = useMemo(
    () => STATUSES.reduce((acc, s) => ({ ...acc, [s.key]: orders.filter(o => o.status === s.key).length }), {}),
    [orders]
  );

  if (!token) {
    return (
      <div className="min-h-screen island-pattern flex items-center justify-center px-4">
        <form
          onSubmit={handleLogin}
          data-testid="kitchen-login-form"
          className="w-full max-w-sm bg-white rounded-3xl border border-[#E5D9C5] p-8 shadow-[0_20px_60px_rgba(44,36,27,0.08)] slide-up"
        >
          <div className="w-12 h-12 rounded-2xl bg-[#D9381E] flex items-center justify-center">
            <Lock className="w-6 h-6 text-white" />
          </div>
          <h1 className="mt-4 font-display font-extrabold text-2xl text-[#2C241B]">Kitchen Access</h1>
          <p className="text-sm text-[#7A6C5D] mt-1">Enter the staff password to view orders.</p>
          <div className="mt-5">
            <Label htmlFor="kitchen-password" className="text-sm font-semibold">Password</Label>
            <Input
              id="kitchen-password"
              type="password"
              data-testid="kitchen-password-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="mt-1 bg-white border-[#E5D9C5] rounded-xl"
            />
          </div>
          <button
            type="submit"
            disabled={loggingIn || !password}
            data-testid="kitchen-login-btn"
            className="cta-primary mt-5 w-full rounded-full py-3 font-display font-extrabold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen island-pattern">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#F9F6F0]/90 border-b border-[#E5D9C5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-display font-extrabold text-2xl text-[#2C241B]">Kitchen Dashboard</h1>
            <p className="text-xs text-[#7A6C5D]">Hafa Adai Kitchen · Live orders & settings</p>
          </div>
          <div className="flex items-center gap-2">
            {tab === "orders" && (
              <button data-testid="kitchen-refresh-btn" onClick={() => load()} className="rounded-full p-2 bg-white border border-[#E5D9C5] hover:bg-[#F5EEE0]">
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            )}
            <button data-testid="kitchen-logout-btn" onClick={logout} className="rounded-full px-3 py-2 bg-white border border-[#E5D9C5] hover:bg-[#F5EEE0] flex items-center gap-1 text-sm">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3 flex items-center gap-2">
          <button
            data-testid="tab-orders"
            onClick={() => setTab("orders")}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${tab === "orders" ? "bg-[#2C241B] text-white border-[#2C241B]" : "bg-white text-[#2C241B] border-[#E5D9C5] hover:bg-[#F5EEE0]"}`}
          >
            <ListChecks className="w-4 h-4" /> Orders
          </button>
          <button
            data-testid="tab-settings"
            onClick={() => setTab("settings")}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${tab === "settings" ? "bg-[#2C241B] text-white border-[#2C241B]" : "bg-white text-[#2C241B] border-[#E5D9C5] hover:bg-[#F5EEE0]"}`}
          >
            <Settings className="w-4 h-4" /> Settings
          </button>
        </div>
        {tab === "orders" && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-3 flex items-center justify-between gap-2 overflow-x-auto">
            <div className="flex gap-2">
              <FilterChip label={`All · ${orders.length}`} active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all" />
              {STATUSES.map(s => (
                <FilterChip key={s.key} label={`${s.label} · ${stats[s.key] || 0}`} active={filter === s.key} onClick={() => setFilter(s.key)} testid={`filter-${s.key}`} />
              ))}
            </div>
            <button
              data-testid="delete-all-orders-btn"
              onClick={handleDeleteAll}
              disabled={orders.length === 0}
              className="shrink-0 rounded-full px-3 py-1.5 text-xs font-bold border border-[#D9381E] text-[#D9381E] hover:bg-[#D9381E] hover:text-white transition-colors flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash className="w-3.5 h-3.5" /> Delete all
            </button>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {tab === "settings" ? (
          <KitchenSettings token={token} />
        ) : (
          <>
            <SlotFillPanel data={slotFills} />
            {orders.length === 0 && !loading && (
              <div className="text-center py-20 text-[#7A6C5D]" data-testid="kitchen-empty">
                No orders yet. New orders will appear here automatically.
              </div>
            )}
            <div data-testid="kitchen-orders-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(o => (
                <OrderCard key={o.id} order={o} onStatus={(s) => changeStatus(o.id, s)} onDelete={() => handleDelete(o.id)} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function SlotFillPanel({ data }) {
  const cap = data?.cap;
  if (!cap) return null;
  const renderDay = (dayLabel, day) => {
    if (!day || !day.fills || Object.keys(day.fills).length === 0) return null;
    const rows = Object.entries(day.fills).sort();
    return (
      <div className="bg-white rounded-2xl border border-[#E5D9C5] p-3 flex-1 min-w-[240px]" data-testid={`slot-fill-${dayLabel.toLowerCase()}`}>
        <div className="text-xs font-bold uppercase tracking-wide text-[#7A6C5D] mb-2">{dayLabel} · cap {cap}</div>
        <div className="flex flex-wrap gap-1.5">
          {rows.map(([slot, count]) => {
            const full = count >= cap;
            return (
              <span
                key={slot}
                data-testid={`slot-fill-pill-${slot.replace(/[\s:]/g, "-")}`}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${full ? "bg-[#FDECE8] text-[#D9381E] border-[#D9381E]/40" : "bg-[#F5EEE0] text-[#2C241B] border-[#E5D9C5]"}`}
              >
                {slot} · {count}/{cap}
              </span>
            );
          })}
        </div>
      </div>
    );
  };
  const anyToday = data?.today && Object.keys(data.today.fills || {}).length > 0;
  const anyTom = data?.tomorrow && Object.keys(data.tomorrow.fills || {}).length > 0;
  if (!anyToday && !anyTom) return null;
  return (
    <div data-testid="slot-fill-panel" className="mb-4 flex flex-wrap gap-3">
      {renderDay("Today", data.today)}
      {renderDay("Tomorrow", data.tomorrow)}
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${active ? "bg-[#2C241B] text-white border-[#2C241B]" : "bg-white text-[#2C241B] border-[#E5D9C5] hover:bg-[#F5EEE0]"}`}
    >
      {label}
    </button>
  );
}

function formatPaymentStatus(status) {
  if (status === "paid") return "Paid";
  if (status === "pay_at_pickup") return "Pay at pickup";
  return status;
}

function OrderCard({ order, onStatus, onDelete }) {
  const statusDef = STATUSES.find(s => s.key === order.status) || STATUSES[0];
  return (
    <div data-testid={`kitchen-order-${order.id}`} className="bg-white rounded-2xl border border-[#E5D9C5] p-5 shadow-[0_6px_20px_rgba(44,36,27,0.05)]">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] text-[#7A6C5D]">Order #{order.id.slice(0, 8).toUpperCase()}</div>
          <div className="font-display font-extrabold text-lg text-[#2C241B]">{order.customer_name}</div>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid={`order-status-${order.id}`} className={`text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${statusDef.color}`}>
            {statusDef.label}
          </span>
          {onDelete && (
            <button
              data-testid={`delete-order-${order.id}`}
              onClick={onDelete}
              title="Delete order"
              className="p-1.5 rounded-full text-[#7A6C5D] hover:text-white hover:bg-[#D9381E] transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 space-y-1 text-sm text-[#2C241B]">
        <div className="flex items-center gap-2 text-[#7A6C5D]"><Phone className="w-3.5 h-3.5" /> {order.customer_phone}</div>
        <div className="flex items-center gap-2 text-[#7A6C5D]"><Clock className="w-3.5 h-3.5" /> {order.pickup_date} · {order.pickup_slot}</div>
      </div>

      <div className="mt-3 pt-3 border-t border-[#E5D9C5] space-y-1">
        {order.items.map(it => (
          <div key={it.id} className="flex justify-between text-sm">
            <span>{it.quantity} × {it.name}</span>
            <span className="font-semibold">${it.line_total.toFixed(2)}</span>
          </div>
        ))}
      </div>
      {order.notes && (
        <div className="mt-2 text-xs text-[#2C5E3B] bg-[#2C5E3B]/10 rounded-lg p-2">Note: {order.notes}</div>
      )}

      <div className="mt-3 flex items-center justify-between">
        <div className="text-xs">
          <span className="text-[#7A6C5D]">Payment:</span>{" "}
          <span className={`font-semibold ${order.payment_status === "paid" ? "text-[#2C5E3B]" : "text-[#B77714]"}`}>
            {formatPaymentStatus(order.payment_status)}
          </span>
        </div>
        <div className="font-display font-extrabold text-lg text-[#D9381E]">${order.total.toFixed(2)}</div>
      </div>

      <div className="mt-4 flex flex-wrap gap-1.5">
        {STATUSES.filter(s => s.key !== order.status).map(s => (
          <button
            key={s.key}
            data-testid={`set-status-${s.key}-${order.id}`}
            onClick={() => onStatus(s.key)}
            className="text-xs font-semibold rounded-full px-3 py-1.5 bg-[#F5EEE0] border border-[#E5D9C5] hover:bg-[#E5D9C5] transition-colors"
          >
            → {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}
